<script setup lang="ts">
import { computed } from 'vue';
import type { Question, QuestionFoil } from '@/types/TestReview';

const props = defineProps<{
    questions: Question[]
}>();

const selectedQuestion = computed(() => props.questions.find((question) => question.visible));

// combine letter and the answer (foil.letter + foil.answer) to make the label
// for the radio button
// this should ideally be done when the data is being fetched from the api
const foils = computed(() => {
    if (!selectedQuestion.value) {
        return [];
    }
    return selectedQuestion.value.foils.map((foil) => ({
        ...foil,
        label: `<strong>${foil.letter})</strong> ${foil.answer}`,
    }));
});

const stemHasImage = () => (selectedQuestion.value ? selectedQuestion.value.stem.includes('<img') : false);

const getAnswerClass = (isCorrect: boolean, isSelected: boolean): string => {
    if (!isSelected) {
        return 'explanation-not-selected';
    }

    return isCorrect ? 'callout-explanation-correct' : 'callout-explanation-incorrect';
};

const isAnswerSelected = (letter: string): boolean => selectedQuestion.value?.selected === letter;

const getCorrectnessLabel = (foil: QuestionFoil): string => {
    const correctString = foil.correct ? 'Correct' : 'Incorrect';
    return isAnswerSelected(foil.letter) ? `${correctString} Answer Selected` : correctString;
};
</script>

<template>
    <!-- eslint-disable max-len -->
    <!-- eslint-disable vue/no-v-html -->
    <q-card
        v-if="selectedQuestion"
        class="q-px-lg q-py-none"
        flat
    >
        <q-card-section
            v-if="selectedQuestion.subUnit"
            class="subunit-text text-fsum-gray3 text-right q-pb-none"
        >
            <span> Subunit {{ selectedQuestion.subUnit.orderNum }}: {{ selectedQuestion.subUnit.title }}</span>
        </q-card-section>
        <q-card-section
            class="align-center question row q-pb-none"
            :horizontal="!stemHasImage"
        >
            <div
                class="text-body text-weight-bold col-fit inline q-pr-md self-start"
            >
                {{ selectedQuestion.number }})
            </div>
            <div
                class="text-body col"
                :class="stemHasImage() ? 'question-images' : ''"
                v-html="selectedQuestion.stem"
            />
        </q-card-section>
        <q-card-section
            class="question-foils q-gutter-y-md"
            role="list"
        >
            <q-item
                v-for="foil in foils"
                :key="foil.letter"
                class="q-pt-none q-pb-sm"
            >
                <div class="row full-width qa-container">
                    <div class="col">
                        <div class="row">
                            <div
                                v-if="!isAnswerSelected(foil.letter)"
                                class="col-12"
                            >
                                <q-radio
                                    v-model="selectedQuestion.selected"
                                    :val="foil.letter"
                                    color="fsum-med-light-gray"
                                    class="answer q-mb-md q-pr-sm"
                                    :aria-label="'Answer ' + foil.letter"
                                    disable
                                    dense
                                >
                                    <span v-html="foil.label" />
                                </q-radio>
                            </div>
                            <div class="col-12">
                                <div
                                    class="column q-py-sm q-px-md"
                                    :class="getAnswerClass(foil.correct, selectedQuestion.selected === foil.letter)"
                                >
                                    <div class="q-pb-md answer-explanation-label">
                                        <q-icon
                                            :name="foil.correct ? 'o_check_circle' : 'o_cancel'"
                                            class="answer-icon q-pr-sm"
                                            :class="foil.correct ? 'correct' : 'incorrect'"
                                        />
                                        <span>
                                            {{ getCorrectnessLabel(foil) }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="isAnswerSelected(foil.letter)"
                                    >
                                        <q-radio
                                            v-model="selectedQuestion.selected"
                                            :val="foil.letter"
                                            color="fsum-med-light-gray"
                                            class="answer q-mb-xs q-pr-sm text-weight-bold"
                                            :aria-label="'Answer ' + foil.letter"
                                            disable
                                            dense
                                        >
                                            <span v-html="foil.label" />
                                        </q-radio>
                                    </div>
                                    <span
                                        class="answer-explanation"
                                        v-html="foil.explanation"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </q-item>
        </q-card-section>
    </q-card>
</template>

<style scoped lang="scss">
.subunit-text {
    font-size: 12px;
}
.question {
    font-size: 20px;

    @media screen and (max-width: $breakpoint-sm-max) {
        font-size: 18px;
    }
}
.answer {
    align-items: start;

    @media screen and (max-width: $breakpoint-sm-max) {
        font-size: 14px;
    }
}
:deep(.question-foils) {
    font-size: 16px;

    @media screen and (max-width: $breakpoint-sm-max) {
        .answer-explanation-label {
            font-size: 14px;
        }
    }

    .answer-explanation-label {
        display: inline-flex;
        align-items: center;
        font-weight: 500;
    }

    .answer-explanation {
        font-size: 14px;
    }

    .q-radio.disabled {
        opacity: 1 !important;
    }
}
:deep(.q-radio__label) {
    padding-left: 16px;
}
.question-images {
    display: inline-flex;
    flex-flow: wrap;
    flex-direction: column;

    :deep(div.cops-images) {
        width: 100%;
        padding-top: 8px;
        flex-direction: column;
        order: 99; // ensures the image is shown after the question
    }
}
:deep(.cops-images) {
    @media (max-width: $breakpoint-lg-min) {
        img {
            width: 100%;
        }
    }
}
.question-nav-button {
    width: 32px;
    height: 32px;
}
.align-center {
    align-items: center;
}
.callout-explanation-correct {
    background-color: var(--fsum-light-green);
    border: 1px solid var(--fsum-green2);
    border-radius: 1px;

    .answer-explanation-label {
        color: var(--fsum-green2);
        font-weight: bold;
    }
}
.callout-explanation-incorrect {
    background-color: var(--fsum-light-red);
    border: 1px solid var(--fsum-dark-red);
    border-radius: 1px;

    .answer-explanation-label {
        color: var(--fsum-dark-red);
        font-weight: bold;
    }
}
.explanation-not-selected {
    border: 1px solid var(--fsum-gray);
    border-radius: 1px;
}
.answer-icon {
    font-size: 20px;

    &.correct {
        color: var(--fsum-green2);
    }
    &.incorrect {
        color: var(--fsum-dark-red);
    }
}
:deep(.q-radio__bg) {
    top: 2px;
    left: 0;
}
</style>
